import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { resetSearch, search } from '../search-actions';
import { getErrors, isFetching } from '../search-with-results/search-results-reducer';
import { getFormActions } from '../../utils/form-helpers';
import formConfig from '../search-form-config';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import Input2 from '../../components/input/input-2/Input2';
import { useLocation, useNavigate } from 'react-router';
import styles from './search.module.less';
import { useFeature } from '../../components/hooks/isFeatureEnabled';
import { CAN_USE_SEMANTIC_SEARCH } from '../../constants/features/constants';
import { ToggleSwitch } from '@sb1/ffe-form-react';

const { setFormField } = getFormActions(formConfig);

const SearchComponent = () => {
    const location = useLocation();
    const searchQueryInLocation = queryString.parse(location.search)?.query || '';
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchField = useRef(null);
    const prevQueryRef = useRef(searchQueryInLocation);
    const [useSemanticSearch, setUseSemanticSearch] = useState(false);
    const prevToggleRef = useRef(useSemanticSearch);

    const isFetchingSearchResults = useSelector((state) => isFetching(state));
    const searchForm = useSelector((state) => state.searchForm);
    const errors = useSelector((state) => getErrors(state));
    const canUseSemanticSearch = useFeature(CAN_USE_SEMANTIC_SEARCH);

    const focusSearchField = () => {
        searchField?.current?.focus();
    };

    const formSubmit = (event) => {
        event.preventDefault();

        const { query } = searchForm;
        const searchString = queryString.stringify({ query });
        navigate({
            ...location,
            search: searchString,
        });
    };

    const beginSearch = async (query) => {
        if (!query || query.trim().length === 0) {
            return;
        }

        dispatch(setFormField('query', query));
        trackEvent('project', 'search', { query });

        return dispatch(search(query, useSemanticSearch));
    };

    useEffect(() => {
        if (searchQueryInLocation) {
            beginSearch(searchQueryInLocation);
        } else {
            focusSearchField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (prevQueryRef.current !== searchQueryInLocation || prevQueryRef.current !== useSemanticSearch) {
            prevQueryRef.current = searchQueryInLocation;
            prevToggleRef.current = useSemanticSearch;
            if (searchQueryInLocation) {
                beginSearch(searchQueryInLocation);
            }
        }
    }, [searchQueryInLocation, useSemanticSearch]);

    useEffect(() => {
        return () => {
            dispatch(setFormField('query', ''));
            dispatch(resetSearch());
        };
    }, [dispatch]);

    const { query } = searchForm;

    return (
        <div className={styles.outerContainer}>
            {canUseSemanticSearch && (
                <ToggleSwitch
                    onChange={(evt) => {
                        setUseSemanticSearch(evt.target.checked);
                    }}
                    checked={useSemanticSearch}
                    hideOnOff
                    className={styles.toggle}
                    description={'Under utvikling. Vises bare for enkelte brukere.'}
                >
                    Skru på semantisk søk
                </ToggleSwitch>
            )}

            <form action="" onSubmit={formSubmit} className={styles.container}>
                <div className={styles.searchInputGroup}>
                    <Input2
                        id="search"
                        className={styles.searchInput}
                        type="search"
                        name="search"
                        title="Navn eller spleisnummer"
                        value={query}
                        error={errors?.global}
                        ref={searchField}
                        onChange={(e) => dispatch(setFormField('query', e.target.value))}
                    />
                </div>
                <RoundedButtonPrimary className={styles.button} disabled={isFetchingSearchResults}>
                    {isFetchingSearchResults ? 'Søker …' : 'Søk'}
                </RoundedButtonPrimary>
            </form>
        </div>
    );
};

export default SearchComponent;
